import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";
import { cn } from "./ui/utils";
import { useAppStore } from "../store/useAppStore";

interface ModalSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  height?: "auto" | "half" | "twoThirds" | "full";
  showHandle?: boolean;
  className?: string;
  /** Extra classes for the scrollable body (e.g. flush padding for iframes). */
  bodyClassName?: string;
}

export function ModalSheet({
  open,
  onClose,
  title,
  children,
  height = "auto",
  showHandle = true,
  className,
  bodyClassName,
}: ModalSheetProps) {
  const { setNavbarVisible } = useAppStore();

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      setNavbarVisible(false);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      setNavbarVisible(true);
    };
  }, [open, handleKeyDown, setNavbarVisible]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — tap above sheet to close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className={cn(
              "fixed bottom-0 left-0 right-0 z-[61] bg-white rounded-t-3xl shadow-[0_-8px_40px_rgba(0,0,0,0.12)]",
              "pb-[env(safe-area-inset-bottom)] flex flex-col min-h-0",
              height === "auto" && "max-h-[min(85vh,100dvh-1rem)]",
              height === "half" && "h-[50dvh]",
              height === "twoThirds" && "h-[66dvh] max-h-[66vh]",
              height === "full" && "h-[95dvh]",
              className
            )}
          >
            {/* Handle */}
            {showHandle && (
              <div className="flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>
            )}

            {/* Header */}
            {title && (
              <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-gray-100 shrink-0">
                <h3 className="text-[14px] font-medium text-gray-900 leading-snug line-clamp-2">
                  {title}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors shrink-0"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
            )}

            {/* Content */}
            <div
              data-sheet-body
              className={cn(
                "flex-1 min-h-0 overscroll-y-contain touch-pan-y",
                bodyClassName ?? "overflow-y-auto px-5 py-4",
              )}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
